/**
 * Whether a ServiceNow field engineer works for a given client, and what the per-company sync changes on the
 * engineer's SmartTeams record.
 *
 * ServiceNow has no "this engineer works for Dell" field. The only place it shows is the engineer's GROUPS in
 * `engineer_list` ("DELL FE Paris", "DELL Client team", …), so an engineer belongs to a company when the name of the
 * company's ServiceNow client appears in one of those group names. Skills are deliberately not looked at.
 *
 * Pure, so the rules can be tested without ServiceNow or a database.
 */

/** The part of an `engineer_list` group the matching needs. */
export interface GroupLike {
  name?: string | null;
}

/** Lowercase, accents and punctuation removed, words separated by single spaces: "Dell - FE Paris" → "dell fe paris". */
export function normalizeName(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * A group belongs to a client when every word of the client's name appears in the group's name as a whole word, in
 * any order and in any letter case. "DELL" matches "DELL FE Paris", "Dell Client team" and "DELL-FE Lyon", but not
 * "Dellwood". A client name made of several words ("Park Land") needs all of them, wherever they sit in the group.
 */
export function groupMatchesClient(groupName: string | null | undefined, clientName: string | null | undefined): boolean {
  const clientWords = normalizeName(clientName).split(' ').filter(Boolean);
  if (clientWords.length === 0) return false;
  const groupWords = new Set(normalizeName(groupName).split(' ').filter(Boolean));
  return clientWords.every(word => groupWords.has(word));
}

/** Whether any of the engineer's groups belongs to the client. */
export function worksForClient(groups: GroupLike[] | null | undefined, clientName: string | null | undefined): boolean {
  return (groups ?? []).some(group => groupMatchesClient(group.name, clientName));
}

/** The part of the SmartTeams engineer record the plan reads. */
export interface ExistingEngineer {
  partnerCompanyIds?: string[] | null;
  isPartner?: boolean | null;
  serviceNowSysId?: string | null;
}

/** What ServiceNow says about the engineer: `partner`, `internal`, or blank when it cannot tell (see engineer-type.ts). */
export type ServiceNowEngineerType = 'partner' | 'internal' | '';

export interface EngineerChangePlan {
  /** Append the company being synced. It goes last: the engineer's first company stays their primary one. */
  addCompany: boolean;
  /**
   * The new partner flag, or undefined to leave it as it is. It follows ServiceNow: `true` when ServiceNow says
   * partner, `false` when it says internal. When ServiceNow cannot tell, the flag is never touched.
   */
  newIsPartner: boolean | undefined;
  /** The engineer has no ServiceNow link yet; store it. An existing link is never replaced. */
  linkServiceNowId: boolean;
  changed: boolean;
}

/**
 * What to change on an existing SmartTeams engineer, found in the synced company's client, so it agrees with
 * ServiceNow: this one company is added (nothing is ever removed from the company list), and the partner flag follows
 * what ServiceNow says.
 */
export function planEngineerUpdate(input: {
  existing: ExistingEngineer;
  /** The company being synced. */
  companyId: string;
  serviceNowType: ServiceNowEngineerType;
}): EngineerChangePlan {
  const addCompany = !(input.existing.partnerCompanyIds ?? []).includes(input.companyId);
  const isPartnerNow = input.existing.isPartner === true;
  let newIsPartner: boolean | undefined;
  if (input.serviceNowType === 'partner' && !isPartnerNow) newIsPartner = true;
  else if (input.serviceNowType === 'internal' && isPartnerNow) newIsPartner = false;
  const linkServiceNowId = !input.existing.serviceNowSysId;
  return {
    addCompany,
    newIsPartner,
    linkServiceNowId,
    changed: addCompany || newIsPartner !== undefined || linkServiceNowId,
  };
}
